import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, MessageSquare } from 'lucide-react';

interface SendInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMethod: (
    method: 'email' | 'sms',
    allowStripePay: boolean,
    depositAmount?: number,
    saveCardOnFile?: boolean,
  ) => void;
  customerEmail?: string;
  customerPhone?: string;
  /** CRM customer id — enables “Save card on file” when paying via Stripe link. */
  customerId?: string;
  hasStripeConnected?: boolean;
  isLoading?: boolean;
  loadingMethod?: 'email' | 'sms' | null;
  bookingTotal?: number;
  amountPaid?: number;
}

export default function SendInvoiceModal({
  isOpen,
  onClose,
  onSelectMethod,
  customerEmail,
  customerPhone,
  customerId,
  hasStripeConnected = false,
  isLoading = false,
  loadingMethod = null,
  bookingTotal = 0,
  amountPaid = 0
}: SendInvoiceModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [emailAllowStripePay, setEmailAllowStripePay] = useState(true);
  const [smsAllowStripePay, setSmsAllowStripePay] = useState(true);
  const [saveCardOnFile, setSaveCardOnFile] = useState(false);
  const [paymentType, setPaymentType] = useState<'full' | 'deposit'>('full');
  const [depositAmount, setDepositAmount] = useState('');
  const [selectedPercentage, setSelectedPercentage] = useState<'25' | '50' | '75' | 'full' | null>(null);

  React.useEffect(() => {
    console.log('SendInvoiceModal - isOpen changed:', isOpen);
    if (isOpen) {
      setIsAnimating(true);
      // Reset deposit state when modal opens
      setPaymentType('full');
      setDepositAmount('');
      setSelectedPercentage(null);
      setSaveCardOnFile(false);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!emailAllowStripePay && !smsAllowStripePay) setSaveCardOnFile(false);
  }, [emailAllowStripePay, smsAllowStripePay]);

  const remainingBalance = Math.round((bookingTotal - amountPaid) * 100) / 100;

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  const handleSelectMethod = (method: 'email' | 'sms') => {
    if (isLoading) return; // Don't allow selection while loading
    
    const allowStripePay = method === 'email' ? emailAllowStripePay : smsAllowStripePay;
    const deposit = paymentType === 'deposit' && depositAmount ? parseFloat(depositAmount) : undefined;
    const saveCard =
      !!customerId &&
      allowStripePay &&
      saveCardOnFile &&
      remainingBalance > 0;

    console.log('SendInvoiceModal - Method selected:', method, 'Allow Stripe Pay:', allowStripePay, 'Deposit:', deposit);
    onSelectMethod(method, allowStripePay, deposit, saveCard);
    
    // Close the modal after selecting method
    handleClose();
  };

  console.log('SendInvoiceModal - Rendering, isOpen:', isOpen, 'isAnimating:', isAnimating);
  
  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[200000] transition-all duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop with blur - iOS style */}
      <div
        className="fixed inset-0 transition-opacity"
        style={{
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto pointer-events-none">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <div 
            className={`relative bg-white rounded-[20px] shadow-xl overflow-hidden transform transition-all duration-300 ease-out ${
              isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
            } w-full max-w-md mx-4 pointer-events-auto`}
          >
            {/* Header - iOS Style */}
            <div className="bg-white border-b border-gray-200/60 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleClose}
                    className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="h-5 w-5" />
                  </button>
                  <p className="text-sm text-gray-500">
                    Choose how to send the invoice to your customer
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Payment Type Selection - Only show if Stripe is connected and there's a balance */}
              {hasStripeConnected && remainingBalance > 0 && (
                <div className="space-y-3 pb-3 border-b border-gray-200">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Type
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentType('full');
                        setDepositAmount('');
                        setSelectedPercentage(null);
                      }}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                        paymentType === 'full'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Full Payment
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPaymentType('deposit');
                        if (!depositAmount) {
                          setDepositAmount('');
                        }
                      }}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                        paymentType === 'deposit'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Deposit
                    </button>
                  </div>

                  {/* Deposit Amount Input - Only show when Deposit is selected */}
                  {paymentType === 'deposit' && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Deposit Amount *
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm font-medium">$</span>
                        </div>
                        <input
                          type="number"
                          value={depositAmount}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = parseFloat(value);
                            
                            if (value === '' || (numValue >= 0 && numValue <= remainingBalance)) {
                              setDepositAmount(value);
                              setSelectedPercentage(null);
                            }
                          }}
                          min="0"
                          step="0.01"
                          max={remainingBalance}
                          className="w-full pl-8 pr-4 py-2.5 text-base bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const amount = (remainingBalance * 0.25).toFixed(2);
                            setDepositAmount(amount);
                            setSelectedPercentage('25');
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            selectedPercentage === '25'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          25%
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const amount = (remainingBalance * 0.50).toFixed(2);
                            setDepositAmount(amount);
                            setSelectedPercentage('50');
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            selectedPercentage === '50'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          50%
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const amount = (remainingBalance * 0.75).toFixed(2);
                            setDepositAmount(amount);
                            setSelectedPercentage('75');
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            selectedPercentage === '75'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          75%
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDepositAmount(remainingBalance.toFixed(2));
                            setSelectedPercentage('full');
                          }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                            selectedPercentage === 'full'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          Full
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        Remaining Balance: ${remainingBalance.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {hasStripeConnected && remainingBalance > 0 && customerId && (
                <div className="flex items-start space-x-2 pb-3 border-b border-gray-200">
                  <input
                    type="checkbox"
                    id="send-invoice-save-card"
                    checked={saveCardOnFile}
                    disabled={!emailAllowStripePay && !smsAllowStripePay}
                    onChange={(e) => setSaveCardOnFile(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label htmlFor="send-invoice-save-card" className="text-sm text-gray-700 cursor-pointer leading-snug">
                    Save card on file when the customer pays with the Stripe link
                  </label>
                </div>
              )}

              {/* Email Option */}
              <button
                onClick={() => handleSelectMethod('email')}
                disabled={
                  !customerEmail || 
                  (isLoading && loadingMethod === 'email') ||
                  (paymentType === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                }
                className={`w-full p-4 rounded-xl border transition-all touch-manipulation ${
                  customerEmail && !(isLoading && loadingMethod === 'email') && !(paymentType === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                    ? 'border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100 cursor-pointer'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-xl ${
                    customerEmail && !(isLoading && loadingMethod === 'email') ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {isLoading && loadingMethod === 'email' ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900">Email</h4>
                    <p className="text-sm text-gray-600">
                      {isLoading && loadingMethod === 'email' 
                        ? 'Sending invoice...' 
                        : customerEmail || 'No email address available'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Stripe Pay Checkbox */}
                {hasStripeConnected && customerEmail && (
                  <div className="mt-3 flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      id="email-stripe-pay"
                      checked={emailAllowStripePay}
                      onChange={(e) => setEmailAllowStripePay(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="email-stripe-pay" className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                      <img src="/Stripe (2).png" alt="Stripe" className="w-6 h-6" />
                      <span>Stripe Payment Link</span>
                    </label>
                  </div>
                )}
              </button>

              {/* SMS Option */}
              <button
                onClick={() => handleSelectMethod('sms')}
                disabled={
                  !customerPhone || 
                  (isLoading && loadingMethod === 'sms') ||
                  (paymentType === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                }
                className={`w-full p-4 rounded-xl border transition-all touch-manipulation ${
                  customerPhone && !(isLoading && loadingMethod === 'sms') && !(paymentType === 'deposit' && (!depositAmount || parseFloat(depositAmount) <= 0))
                    ? 'border-gray-200 bg-white hover:bg-gray-50 active:bg-gray-100 cursor-pointer'
                    : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-xl ${
                    customerPhone && !(isLoading && loadingMethod === 'sms') ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {isLoading && loadingMethod === 'sms' ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MessageSquare className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-semibold text-gray-900">SMS</h4>
                    <p className="text-sm text-gray-600">
                      {isLoading && loadingMethod === 'sms' 
                        ? 'Sending invoice...' 
                        : customerPhone || 'No phone number available'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Stripe Pay Checkbox */}
                {hasStripeConnected && customerPhone && (
                  <div className="mt-3 flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      id="sms-stripe-pay"
                      checked={smsAllowStripePay}
                      onChange={(e) => setSmsAllowStripePay(e.target.checked)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <label htmlFor="sms-stripe-pay" className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                      <img src="/Stripe (2).png" alt="Stripe" className="w-6 h-6" />
                      <span>Stripe Payment Link</span>
                    </label>
                  </div>
                )}
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-200/60">
              <button
                onClick={handleClose}
                className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}